import {
    Colors,
    COMPONENT_RADIUS,
    defaultHover,
    FIXED_CIRCLE_DIMENSIONS,
    FOCUS_RING_NO_RADIUS,
    Heading,
    Icon,
    Modal,
    useDialogFocus,
} from '@amelio/ui-library';
import { useId } from 'react';

import { SURFACE_BORDER } from './surfaces';

import type { FormEvent, ReactNode } from 'react';

/*
 * Provisional: to be replaced by `ModalDialog` from `@amelio/ui-library` once its floating chrome can
 * put the close button on the row of its header extra. Today that chrome gives the close button a
 * row of its own above the step bar, which leaves it floating over everything instead of beside the
 * steps.
 *
 * The card's radius is the design's 32px (`rounded-4xl`) and its padding the design's 40px
 * (`p-10`), spelt out so the window's chrome reads as one block.
 */
const CARD = `rounded-[32px] ${SURFACE_BORDER} bg-white p-[40px] shadow-2xl`;

/**
 * The room the window keeps inside its own edges for what its controls draw *outside* themselves.
 * `Modal` clips whatever overflows its panel, and the chrome's controls — the close button, the
 * first and last step pills, the navigation buttons — sit flush with that edge. `FOCUS_RING_NO_RADIUS`
 * paints `ring-offset-2` and then `ring-2`, so its ring reaches four pixels past the control it
 * decorates; without this inset the right side of it is sliced off, as is the close button's own
 * 5 % growth under the pointer. Derived from that ring, not measured off a screenshot: change the
 * ring and this changes with it.
 */
const FOCUS_RING_BLEED = 'p-[4px]';

/**
 * The window's own width, the design's 887px. The library's `MODAL_DIALOG_WIDTHS` scale has no step
 * here — `lg` is 1024px — and widening that step would carry every other dialog with it, so this
 * window states its width itself, in one place.
 */
const DIALOG_WIDTH = 'w-full max-w-[887px]';

interface PlanningDialogShellProps {
    isOpen: boolean;
    onClose: () => void;
    /** Names the window for assistive technology; the visible titles belong to the steps. */
    title: string;
    closeAriaLabel: string;
    /** Left of the close button, on the veil above the card. */
    stepBar: ReactNode;
    /** Below the card, on the veil. */
    footer: ReactNode;
    /** The card and its footer form one `<form>`, so Enter does what the forward button does. */
    onSubmit: (event: FormEvent<HTMLFormElement>) => void;
    dataTestId: string;
    children: ReactNode;
}

/**
 * The creation flow's window: step bar and close button on one row, the step in a card below them,
 * the navigation under the card. Keeps the dialog contract of the library's `ModalDialog` — `dialog`
 * role named by its title, focus trapped then given back, Escape and the veil closing it.
 */
export const PlanningDialogShell = ({
    isOpen,
    onClose,
    title,
    closeAriaLabel,
    stepBar,
    footer,
    onSubmit,
    dataTestId,
    children,
}: PlanningDialogShellProps) => {
    const titleId = `${useId()}-title`;
    const { dialogRef, handleKeyDown } = useDialogFocus(isOpen);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            // Transparent: the library `Modal` then draws no panel at all, and the card below is the
            // only surface.
            bgColor={'bg-transparent'}
            scrollable={false}
            backdropBlur
            zIndex={50}
            className={`${DIALOG_WIDTH} ${FOCUS_RING_BLEED} flex flex-col`}
            dataTestId={dataTestId}
        >
            <div
                ref={dialogRef}
                role={'dialog'}
                aria-modal={'true'}
                aria-labelledby={titleId}
                onKeyDown={handleKeyDown}
                className={'flex min-h-0 flex-1 flex-col'}
                data-testid={`${dataTestId}-dialog`}
            >
                <Heading
                    level={3}
                    id={titleId}
                    className={'sr-only'}
                >
                    {title}
                </Heading>

                <form
                    onSubmit={onSubmit}
                    noValidate
                    className={'flex min-h-0 flex-1 flex-col gap-6'}
                >
                    <div className={'flex flex-shrink-0 items-center justify-between gap-4'}>
                        {stepBar}

                        <button
                            type={'button'}
                            onClick={onClose}
                            aria-label={closeAriaLabel}
                            data-testid={`${dataTestId}-close`}
                            className={`${FOCUS_RING_NO_RADIUS} ${COMPONENT_RADIUS.full} ${defaultHover} flex ${FIXED_CIRCLE_DIMENSIONS.md} flex-shrink-0 cursor-pointer items-center justify-center bg-white/80 hover:bg-white`}
                        >
                            <Icon
                                name={'essential-close'}
                                color={Colors.BASE_500}
                                size={'sm'}
                                ariaHidden
                            />
                        </button>
                    </div>

                    <div className={`scrollbar-glass min-h-0 flex-1 overflow-y-auto ${CARD}`}>
                        {children}
                    </div>

                    <div className={'flex-shrink-0'}>{footer}</div>
                </form>
            </div>
        </Modal>
    );
};
